import type { FC } from 'react';
import { Ban, HardHat, Repeat, Scissors } from 'lucide-react';
import { defineMessages, useIntl } from 'react-intl';

import type { A2uiNodeViewProps } from '@/app/lib/a2ui/catalog.tsx';
import { arr, num, optStr, str } from '@/app/lib/a2ui/props.ts';

const messages = defineMessages({
  culturalFirst: {
    id: 'sprayPlan.culturalFirst',
    defaultMessage: 'Do this first — costs nothing',
    description: 'Section heading above the free field steps that come before any spray.',
  },
  rounds: {
    id: 'sprayPlan.rounds',
    defaultMessage: 'Spray rounds',
    description: 'Heading above the scheduled spray rounds.',
  },
  roundNumber: {
    id: 'sprayPlan.roundNumber',
    defaultMessage: 'Round {number}',
    description: 'Label for one spray round in the schedule.',
  },
  dose: {
    id: 'sprayPlan.dose',
    defaultMessage: 'Per pump (15 L)',
    description: 'Label before the dose measured per 15 litre knapsack sprayer.',
  },
  total: {
    id: 'sprayPlan.total',
    defaultMessage: 'For your plot',
    description: 'Label before the total product quantity needed for the farmer’s acreage.',
  },
  phi: {
    id: 'sprayPlan.phi',
    defaultMessage: 'Pre-harvest interval — {days} days',
    description: 'Heading of the hard stop between the last spray and harvest.',
  },
  rotation: {
    id: 'sprayPlan.rotation',
    defaultMessage: 'Rotate the next spray',
    description: 'Label before the chemical group to switch to, to avoid resistance.',
  },
});

interface Round {
  when: string;
  product: string;
  dosePerPump: string;
  totalQuantity?: string;
  note?: string;
}

function readSteps(value: unknown): string[] {
  return arr(value).flatMap((entry) => {
    if (typeof entry !== 'object' || entry === null) {
      return [];
    }
    const step = str((entry as Record<string, unknown>).step);
    return step ? [step] : [];
  });
}

function readRounds(value: unknown): Round[] {
  return arr(value).flatMap((entry) => {
    if (typeof entry !== 'object' || entry === null) {
      return [];
    }
    const record = entry as Record<string, unknown>;
    const when = str(record.when);
    const product = str(record.product);
    if (!when || !product) {
      return [];
    }
    return [
      {
        when,
        product,
        dosePerPump: str(record.dosePerPump),
        totalQuantity: optStr(record.totalQuantity),
        note: optStr(record.note),
      },
    ];
  });
}

export const SprayPlan: FC<A2uiNodeViewProps> = ({ node }) => {
  const intl = useIntl();
  const heading = str(node.props.heading);
  const culturalSteps = readSteps(node.props.culturalSteps);
  const rounds = readRounds(node.props.rounds);
  const phiDays = num(node.props.phiDays) ?? 0;
  const phiLine = str(node.props.phiLine);
  const rotationLine = optStr(node.props.rotationLine);
  const gearLine = str(node.props.gearLine);
  const exportNote = optStr(node.props.exportNote);

  return (
    <div className="w-full">
      <h2 className="mb-4 text-balance text-2xl font-bold leading-tight tracking-tight text-foreground">
        {heading}
      </h2>

      {culturalSteps.length > 0 ? (
        <section className="mb-5 rounded-lg border border-border bg-card p-5">
          <p className="mb-2.5 flex items-center gap-2 text-sm font-bold uppercase tracking-caps text-primary">
            <Scissors className="size-4 shrink-0" aria-hidden />
            {intl.formatMessage(messages.culturalFirst)}
          </p>
          <ul className="space-y-2">
            {culturalSteps.map((step) => (
              <li key={step} className="flex items-start gap-2.5 text-base text-foreground">
                <span className="mt-2 size-1.5 shrink-0 rounded-full bg-primary" aria-hidden />
                <span className="min-w-0">{step}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {rounds.length > 0 ? (
        <section className="mb-5">
          <p className="mb-2.5 text-sm font-bold uppercase tracking-caps text-muted-foreground">
            {intl.formatMessage(messages.rounds)}
          </p>
          <ol className="space-y-3">
            {rounds.map((round, index) => (
              <li
                key={`${round.when}-${round.product}`}
                className="rounded-lg border-l-4 border-primary bg-card p-4 shadow-elevated"
              >
                <div className="mb-2 flex flex-wrap items-baseline gap-x-2 gap-y-1">
                  <span className="rounded-sm bg-primary px-2 py-0.5 text-xs font-bold uppercase tracking-caps text-primary-foreground">
                    {intl.formatMessage(messages.roundNumber, { number: index + 1 })}
                  </span>
                  <span className="min-w-0 text-base font-bold text-foreground">{round.when}</span>
                </div>
                <p className="mb-2 text-lg font-bold leading-snug text-foreground" translate="no">
                  {round.product}
                </p>
                <dl className="grid gap-2 sm:grid-cols-2">
                  <div className="rounded-md bg-muted p-3">
                    <dt className="text-xs font-semibold uppercase tracking-caps text-muted-foreground">
                      {intl.formatMessage(messages.dose)}
                    </dt>
                    <dd className="text-base font-bold tabular-nums text-foreground">
                      {round.dosePerPump}
                    </dd>
                  </div>
                  {round.totalQuantity ? (
                    <div className="rounded-md bg-muted p-3">
                      <dt className="text-xs font-semibold uppercase tracking-caps text-muted-foreground">
                        {intl.formatMessage(messages.total)}
                      </dt>
                      <dd className="text-base font-bold tabular-nums text-foreground">
                        {round.totalQuantity}
                      </dd>
                    </div>
                  ) : null}
                </dl>
                {round.note ? (
                  <p className="mt-2 text-sm leading-snug text-muted-foreground">{round.note}</p>
                ) : null}
              </li>
            ))}
          </ol>
        </section>
      ) : null}

      <section className="mb-4 rounded-lg border-2 border-destructive/60 bg-destructive/10 p-5">
        <p className="mb-1.5 flex items-center gap-2 text-sm font-bold uppercase tracking-caps text-destructive">
          <Ban className="size-4 shrink-0" aria-hidden />
          {intl.formatMessage(messages.phi, { days: Math.round(phiDays) })}
        </p>
        <p className="text-base font-semibold leading-snug text-foreground">{phiLine}</p>
        {exportNote ? (
          <p className="mt-2 text-sm leading-snug text-foreground">{exportNote}</p>
        ) : null}
      </section>

      {rotationLine ? (
        <p className="mb-3 flex items-start gap-2 text-sm text-foreground">
          <Repeat className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
          <span className="min-w-0">
            <span className="font-semibold">{intl.formatMessage(messages.rotation)}: </span>
            {rotationLine}
          </span>
        </p>
      ) : null}

      {gearLine ? (
        <p className="flex items-start gap-2 text-sm text-muted-foreground">
          <HardHat className="mt-0.5 size-4 shrink-0" aria-hidden />
          <span className="min-w-0">{gearLine}</span>
        </p>
      ) : null}
    </div>
  );
};
